/**
 * Validation module for the Config Generator page.
 *
 * Provides validators for:
 *   - Pre-populate inputs (tool, business knowledge, root questions)
 *   - Config sections before YAML generation (ranges, uniqueness, etc.)
 *   - Reusable range and uniqueness helpers
 *   - Temporal parameter auto-detection
 */

import {
  ConfigPageState,
  TemplateDefinition,
  ValidationErrors,
} from './types';

// ─── Range Boundaries ──────────────────────────────────────────────────

const PROBABILITY_MIN = 0;
const PROBABILITY_MAX = 1;

// ─── Input Validation ──────────────────────────────────────────────────

/**
 * Validate the three required inputs before pre-population:
 * tool selection, business knowledge, and root questions.
 *
 * Returns errors keyed by field name for exactly the fields that are
 * empty or null.
 */
export function validateInputs(state: ConfigPageState): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!state.selectedToolId) {
    errors.selectedToolId = 'Please select a tool';
  }

  if (!state.businessKnowledge.trim()) {
    errors.businessKnowledge = 'Business knowledge is required';
  }

  if (!state.rootQuestions.trim()) {
    errors.rootQuestions = 'At least one sample question is required';
  }

  return errors;
}

// ─── Config Section Validation ─────────────────────────────────────────

/**
 * Validate all config sections before YAML generation.
 *
 * Checks temperature ranges, weight ranges, probability ranges,
 * non-empty `expands_to`, and template ID uniqueness.
 */
export function validateConfigSections(
  state: ConfigPageState,
): ValidationErrors {
  const errors: ValidationErrors = {};

  // LLM temperature ranges
  const polisherTempError = validateRangeField(
    state.llm.polisher.temperature,
    PROBABILITY_MIN,
    PROBABILITY_MAX,
    'Polisher temperature',
  );
  if (polisherTempError) {
    errors['llm.polisher.temperature'] = polisherTempError;
  }

  const validatorTempError = validateRangeField(
    state.llm.validator.temperature,
    PROBABILITY_MIN,
    PROBABILITY_MAX,
    'Validator temperature',
  );
  if (validatorTempError) {
    errors['llm.validator.temperature'] = validatorTempError;
  }

  // Dimensions selection probability
  const dimProbError = validateRangeField(
    state.dimensionsSelection.probability,
    PROBABILITY_MIN,
    PROBABILITY_MAX,
    'Dimensions probability',
  );
  if (dimProbError) {
    errors['dimensionsSelection.probability'] = dimProbError;
  }

  // OrderBy selection probability
  const orderByProbError = validateRangeField(
    state.orderBySelection.probability,
    PROBABILITY_MIN,
    PROBABILITY_MAX,
    'OrderBy selection probability',
  );
  if (orderByProbError) {
    errors['orderBySelection.probability'] = orderByProbError;
  }

  // Group expansion probability
  const groupExpProbError = validateRangeField(
    state.groupExpansionProbability,
    PROBABILITY_MIN,
    PROBABILITY_MAX,
    'Group expansion probability',
  );
  if (groupExpProbError) {
    errors['groupExpansionProbability'] = groupExpProbError;
  }

  // Template weights and ID uniqueness per tool
  for (const [toolName, templates] of Object.entries(state.templates)) {
    const enabledTemplates = templates.filter((t) => t.enabled);

    // Weight range validation
    for (const tmpl of enabledTemplates) {
      const weightError = validateRangeField(
        tmpl.weight,
        PROBABILITY_MIN,
        PROBABILITY_MAX,
        `Template "${tmpl.id}" weight`,
      );
      if (weightError) {
        errors[`templates.${toolName}.${tmpl._stableKey}.weight`] = weightError;
      }
    }

    // ID uniqueness
    const duplicates = validateUniqueIds(enabledTemplates);
    for (const dupId of duplicates) {
      // Mark all templates sharing this duplicate ID
      for (const tmpl of enabledTemplates) {
        if (tmpl.id === dupId) {
          errors[`templates.${toolName}.${tmpl._stableKey}.id`] =
            `Duplicate template ID: "${dupId}"`;
        }
      }
    }
  }

  // Group expansion: non-empty expands_to
  for (const [paramName, entries] of Object.entries(state.groupExpansion)) {
    for (let i = 0; i < entries.length; i++) {
      if (entries[i].expands_to.length === 0) {
        errors[`groupExpansion.${paramName}[${i}].expands_to`] =
          'At least one member value is required';
      }
    }
  }

  return errors;
}

// ─── Reusable Validators ───────────────────────────────────────────────

/**
 * Validate that a numeric value falls within [min, max] (inclusive).
 *
 * Returns an error message string if out of range, or `null` if valid.
 */
export function validateRangeField(
  value: number,
  min: number,
  max: number,
  fieldName: string,
): string | null {
  if (typeof value !== 'number' || isNaN(value)) {
    return `${fieldName} must be a number`;
  }
  if (value < min || value > max) {
    return `${fieldName} must be between ${min} and ${max}`;
  }
  return null;
}

/**
 * Find duplicate IDs in a list of template definitions.
 *
 * Returns an array of IDs that appear more than once. If all IDs are
 * unique, returns an empty array.
 */
export function validateUniqueIds(
  templates: TemplateDefinition[],
): string[] {
  const seen = new Map<string, number>();
  for (const t of templates) {
    seen.set(t.id, (seen.get(t.id) ?? 0) + 1);
  }

  const duplicates: string[] = [];
  for (const [id, count] of seen.entries()) {
    if (count > 1) {
      duplicates.push(id);
    }
  }
  return duplicates;
}

/**
 * Auto-detect the temporal parameter from a list of parameter names.
 *
 * Returns the first parameter name containing the substring "date"
 * (case-insensitive). If none match, returns an empty string.
 */
export function detectTemporalParameter(
  parameterNames: string[],
): string {
  const dateLower = 'date';
  for (const name of parameterNames) {
    if (name.toLowerCase().includes(dateLower)) {
      return name;
    }
  }
  return '';
}
